import { useCallback, useEffect, useState } from "react";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Toolbar } from "../components/Toolbar";
import { fetchLanguages, saveLanguageLabels, type LanguageDto } from "../lib/languagesApi";
import {
  LABEL_FIELD_META,
  defaultSectionLabels,
  type SectionLabels,
} from "../lib/sectionLabels";

export function LanguagesPage() {
  const [languages, setLanguages] = useState<LanguageDto[]>([]);
  const [selected, setSelected] = useState<string>("en");
  const [labels, setLabels] = useState<SectionLabels>(() => defaultSectionLabels("en"));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLanguages();
      setLanguages(data.languages);
      setWarnings(data.warnings ?? []);
      const first = data.languages[0];
      if (first) {
        setSelected(first.code);
        setLabels({ ...first.labels });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load languages.");
      setLanguages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function onSelect(code: string) {
    const lang = languages.find((row) => row.code === code);
    setSelected(code);
    setLabels(lang ? { ...lang.labels } : defaultSectionLabels(code));
    setSaved(false);
  }

  async function onSave() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await saveLanguageLabels(selected, labels);
      setLanguages((prev) =>
        prev.map((row) => (row.code === updated.code ? updated : row)),
      );
      setLabels({ ...updated.labels });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  function onResetDefaults() {
    setLabels(defaultSectionLabels(selected));
    setSaved(false);
  }

  const current = languages.find((row) => row.code === selected);

  return (
    <AppShell>
      <PageHeader
        eyebrow="Reference"
        title="Languages"
        description={
          <>
            Section display labels by language. Scrapes store <code>kind</code> only; the app and CMS
            resolve titles from this table (<code>Language.labelsJSON</code>).
          </>
        }
        actions={
          <Toolbar>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={busy || loading}
              onClick={onResetDefaults}
            >
              Reset defaults
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || loading}
              onClick={() => void onSave()}
            >
              {busy ? "Saving…" : "Save labels"}
            </button>
          </Toolbar>
        }
      />

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}
      {warnings.map((warning) => (
        <div key={warning} className="error-banner" role="status" style={{ marginBottom: 12 }}>
          {warning}
        </div>
      ))}

      {loading ? (
        <div className="panel panel-pad" role="status">
          Loading languages…
        </div>
      ) : (
        <>
          <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
            <div className="section-title">
              <h2>Languages</h2>
              <p>Active CloudKit <code>Language</code> records (or built-in defaults).</p>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Default source</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {languages.map((lang) => (
                    <tr
                      key={lang.code}
                      className={lang.code === selected ? "row-selected" : undefined}
                      style={{ cursor: "pointer" }}
                      onClick={() => onSelect(lang.code)}
                    >
                      <td>
                        <code>{lang.code}</code>
                      </td>
                      <td>{lang.name}</td>
                      <td>
                        <code>{lang.defaultSource ?? "—"}</code>
                      </td>
                      <td>
                        <span className="chip">{lang.isActive ? "Active" : "Inactive"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel panel-pad stack">
            <div className="section-title">
              <h2>Section labels · {current?.name ?? selected}</h2>
              <p>
                Used on the day review page and by clients instead of scraped headings.
              </p>
            </div>

            <div className="label-config-grid">
              {LABEL_FIELD_META.map((field) => (
                <label key={field.key} className="field">
                  <span>
                    <code>{field.key}</code>
                    <span className="muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                      {field.hint}
                    </span>
                  </span>
                  <input
                    value={labels[field.key]}
                    onChange={(e) => {
                      setLabels((prev) => ({ ...prev, [field.key]: e.target.value }));
                      setSaved(false);
                    }}
                    disabled={busy}
                  />
                </label>
              ))}
            </div>

            {saved && (
              <div className="banner-ok" role="status">
                Labels saved for <code>{selected}</code>. If CloudKit rejects{" "}
                <code>labelsJSON</code>, re-import the updated{" "}
                <code>gospelgo-schema-v1.ckdb</code> then seed again.
              </div>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}
